import React, { useState } from 'react';
import { Card, Space, Typography, Button, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { FilePdfOutlined, FileOutlined } from '@ant-design/icons';
import { useTheme } from '../../../../../Shared/GlobalContext/ThemeContext';
import AudioMessage from '../../../MessageContent/MessageList/MessageBubble/AudioMessage/AudioMessage';
import { resolveMediaDisplayType } from '../../../MessageContent/MessageList/MessageBubble/MessageMediaGallery/MessageMediaGallery';
import { formatDuration } from '../../../../utils/messageRenderUtils';

const { Text } = Typography;

const formatProcessingStage = (stage, t) => {
    if (!stage) return null;
    const key = `media_processing_stage.${stage}`;
    const fallback = String(stage)
        .split('_')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    return t(key, { defaultValue: fallback });
};

const MessageMediaDetails = ({ medias }) => {
    const { t } = useTranslation();
    const { isDarkMode } = useTheme();
    const [videoDurations, setVideoDurations] = useState({});

    if (!medias || medias.length === 0) return null;

    const themeColors = {
        cardBackground: isDarkMode ? '#1f1f1f' : '#fff',
        text: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.88)',
        secondaryText: isDarkMode ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.45)',
    };

    const getMediaCardBodyStyle = (mediaType) => ({
        padding: mediaType === 'audio' ? '12px 16px' : 12,
        minHeight: 'auto',
    });

    return (
        <Card
            title={t('media')}
            style={{ background: themeColors.cardBackground }}
            styles={{ body: { padding: 16 } }}
        >
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                {medias.map((media, index) => {
                    const displayType = resolveMediaDisplayType(media);

                    return (
                        <Card
                            key={index}
                            size="small"
                            style={{ background: themeColors.cardBackground }}
                            styles={{ body: getMediaCardBodyStyle(displayType) }}
                        >
                            {displayType === 'audio' && (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: 10,
                                width: '100%'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <AudioMessage url={media.url} duration={media.data_json?.duration} sender={'customer'} />
                                </div>
                                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                    {typeof media.data_json?.duration === 'number' && (
                                        <Text style={{ color: themeColors.text }}>
                                            {t('duration')}: {formatDuration(media.data_json.duration)}
                                        </Text>
                                    )}
                                    {media.processing_stage && (
                                        <Text style={{ color: themeColors.text }}>
                                            {t('progress_status')}: {formatProcessingStage(media.processing_stage, t)}
                                        </Text>
                                    )}
                                    {media.data_json?.transcription && (
                                        <div style={{
                                            marginTop: 8,
                                            padding: 8,
                                            background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                                            borderRadius: 4
                                        }}>
                                            <Text style={{ color: themeColors.text }}>
                                                <span style={{ fontWeight: 500 }}>{t('transcription')}:</span> {media.data_json.transcription}
                                            </Text>
                                        </div>
                                    )}
                                </Space>
                            </div>
                        )}
                        {displayType === 'video' && (
                            <Space direction="vertical">
                                <div style={{
                                    background: isDarkMode ? '#f0f0f0' : '#ffffff',
                                    padding: 4,
                                    borderRadius: 4,
                                    display: 'inline-block'
                                }}>
                                    <video
                                        controls
                                        src={media.url}
                                        style={{ maxWidth: '100%', borderRadius: 4 }}
                                        onLoadedMetadata={(e) => {
                                            const d = e.currentTarget.duration || 0;
                                            if (!videoDurations[index] && d) {
                                                setVideoDurations((prev) => ({ ...prev, [index]: Math.floor(d) }));
                                            }
                                        }}
                                    />
                                </div>
                                {(media.data_json?.width && media.data_json?.height) && (
                                    <Text style={{ color: themeColors.text }}>{t('dimensions')}: {media.data_json.width}x{media.data_json.height}</Text>
                                )}
                                {(typeof media.data_json?.duration === 'number' || (videoDurations[index] && videoDurations[index] > 0)) && (
                                    <Text style={{ color: themeColors.text }}>
                                        {t('duration')}: {formatDuration(typeof media.data_json?.duration === 'number' ? media.data_json.duration : videoDurations[index])}
                                    </Text>
                                )}
                                {typeof media.data_json?.has_audio !== 'undefined' && (
                                    <Text style={{ color: themeColors.text }}>{t('has_audio')}: {media.data_json.has_audio ? t('yes') : t('no')}</Text>
                                )}
                                {media.processing_stage && (
                                    <Text style={{ color: themeColors.text }}>{t('progress_status')}: {formatProcessingStage(media.processing_stage, t)}</Text>
                                )}
                                {media.data_json?.transcription && (
                                    <div style={{
                                        marginTop: 8,
                                        padding: 8,
                                        background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                                        borderRadius: 4
                                    }}>
                                        <Text style={{ color: themeColors.text, whiteSpace: 'pre-wrap' }}>
                                            <span style={{ fontWeight: 500 }}>{t('transcription')}:</span> {media.data_json.transcription}
                                        </Text>
                                    </div>
                                )}
                                {media.data_json && media.data_json.content_description && (
                                    <div style={{
                                        marginTop: 4,
                                        padding: 8,
                                        background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                                        borderRadius: 4,
                                        position: 'relative'
                                    }}>
                                        <Text
                                            style={{
                                                display: 'block',
                                                fontSize: '13px',
                                                color: themeColors.secondaryText,
                                                lineHeight: 1.4,
                                                fontStyle: 'italic',
                                                whiteSpace: 'pre-wrap'
                                            }}
                                        >
                                            <span style={{ fontWeight: 500 }}>{t('ai_image_analysis')}:</span> {media.data_json.content_description}
                                        </Text>
                                        <Tooltip
                                            title={t('ai_description_tooltip')}
                                            placement="topRight"
                                        >
                                            <span style={{
                                                position: 'absolute',
                                                insetInlineEnd: 4,
                                                top: 4,
                                                fontSize: '9px',
                                                cursor: 'help',
                                                color: themeColors.secondaryText
                                            }}>
                                                🤖
                                            </span>
                                        </Tooltip>
                                    </div>
                                )}
                            </Space>
                        )}
                        {displayType === 'image' && (
                            <Space direction="vertical">
                                <div style={{
                                    background: isDarkMode ? '#f0f0f0' : '#ffffff',
                                    padding: 4,
                                    borderRadius: 4,
                                    display: 'inline-block'
                                }}>
                                    <img
                                        src={media.url}
                                        alt="Message attachment"
                                        style={{ maxWidth: '100%', borderRadius: 4 }}
                                    />
                                </div>
                                {(media.data_json?.width && media.data_json?.height) && (
                                    <Text style={{ color: themeColors.text }}>{t('dimensions')}: {media.data_json.width}x{media.data_json.height}</Text>
                                )}
                                {media.processing_stage && (
                                    <Text style={{ color: themeColors.text }}>{t('progress_status')}: {formatProcessingStage(media.processing_stage, t)}</Text>
                                )}
                                {media.data_json && media.data_json.content_description && (
                                    <div style={{
                                        marginTop: 4,
                                        padding: 8,
                                        background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                                        borderRadius: 4,
                                        position: 'relative'
                                    }}>
                                        <Text
                                            style={{
                                                display: 'block',
                                                fontSize: '13px',
                                                color: themeColors.secondaryText,
                                                lineHeight: 1.4,
                                                fontStyle: 'italic',
                                                whiteSpace: 'pre-wrap'
                                            }}
                                        >
                                            <span style={{ fontWeight: 500 }}>{t('ai_image_analysis')}:</span> {media.data_json.content_description}
                                        </Text>
                                        <Tooltip
                                            title={t('ai_description_tooltip')}
                                            placement="topRight"
                                        >
                                            <span style={{
                                                position: 'absolute',
                                                insetInlineEnd: 4,
                                                top: 4,
                                                fontSize: '9px',
                                                cursor: 'help',
                                                color: themeColors.secondaryText
                                            }}>
                                                🤖
                                            </span>
                                        </Tooltip>
                                    </div>
                                )}
                            </Space>
                        )}
                        {(displayType === 'document' || displayType === 'pdf') && (
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Space>
                                    {displayType === 'pdf' ?
                                        <FilePdfOutlined style={{ fontSize: 24, color: isDarkMode ? '#ff6b6b' : '#ff4d4f' }} /> :
                                        <FileOutlined style={{ fontSize: 24, color: isDarkMode ? '#52c1ff' : '#1890ff' }} />
                                    }
                                    <Text style={{ color: themeColors.text }}>{media.filename || t('document')}</Text>
                                </Space>

                                <Button
                                    type="primary"
                                    size="small"
                                    onClick={() => window.open(media.url, '_blank')}
                                    style={{ marginTop: 8 }}
                                >
                                    {t('view_document')}
                                </Button>

                                {media.processing_stage && (
                                    <Text style={{ color: themeColors.text }}>{t('progress_status')}: {formatProcessingStage(media.processing_stage, t)}</Text>
                                )}
                            </Space>
                        )}
                        </Card>
                    );
                })}
            </Space>
        </Card>
    );
};

export default MessageMediaDetails;
